import { useState, useEffect, useRef } from 'react';
import { X, Upload, Loader2, ExternalLink, AlertTriangle, RotateCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateCatalogProduct,
  useUpdateCatalogProduct,
  usePresignCatalogPhoto,
  useCatalogDetail,
} from '@/hooks/useCatalog';
import { CatalogMultiSelect, type CatalogItem } from './CatalogMultiSelect';
import { api } from '@/lib/api';

const CATEGORIES = [
  'Pneus', 'Freios', 'Suspensão', 'Motor', 'Transmissão', 'Elétrica',
  'Ar condicionado', 'Funilaria', 'Lubrificantes', 'Filtros', 'Baterias', 'Acessórios',
];

interface Props {
  productId?: string | null;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

export function CatalogProductModal({ productId, onClose, onCreated }: Props) {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';

  const { data: detail, isError: detailError, refetch: retryLoad } = useCatalogDetail(pid, productId ?? '');
  const existing = detail?.product;
  // Ver a nota equivalente no ProductDrawer.
  const loadFailed = !!productId && detailError && !existing;

  const createMutation = useCreateCatalogProduct(pid);
  const updateMutation = useUpdateCatalogProduct(pid);
  const presignMutation = usePresignCatalogPhoto(pid);

  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [sku, setSku] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [buyUrl, setBuyUrl] = useState('');
  const [deliveryMin, setDeliveryMin] = useState('');
  const [deliveryMax, setDeliveryMax] = useState('');
  const [tags, setTags] = useState('');
  const [active, setActive] = useState(true);
  const [outOfStock, setOutOfStock] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoS3Key, setPhotoS3Key] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedModels, setSelectedModels] = useState<CatalogItem[]>([]);
  const [modelsLoadFailed, setModelsLoadFailed] = useState(false);
  const [compatMode, setCompatMode] = useState<'universal' | 'specific'>('universal');
  const [linkTestStatus, setLinkTestStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setBrand(existing.brand ?? '');
    setSku(existing.sku ?? '');
    setPartNumber(existing.partNumber ?? '');
    setDescription(existing.description ?? '');
    setCategory(existing.category ?? '');
    setPrice(existing.price ? String(Number(existing.price)) : '');
    setBuyUrl(existing.buyUrl ?? '');
    setDeliveryMin(existing.deliveryDaysMin?.toString() ?? '');
    setDeliveryMax(existing.deliveryDaysMax?.toString() ?? '');
    setTags(existing.tags?.join(', ') ?? '');
    setActive(existing.active);
    setOutOfStock(existing.outOfStock);
    setPhotoUrl(existing.photoUrl ?? '');
    setPhotoS3Key(existing.photoS3Key ?? '');

    const ids = existing.compatibleVehicleIds;
    if (ids && ids.length > 0) {
      setCompatMode('specific');
      setModelsLoadFailed(false);
      api.get('/vehicle-catalog/by-ids', { params: { ids: ids.join(',') } })
        .then((res) => {
          setSelectedModels((res.data as { items: CatalogItem[] }).items);
          setModelsLoadFailed(false);
        })
        // Aqui era pior que no ProductDrawer: `compatMode` ja foi para
        // 'specific' na linha acima, entao a tela afirmava "compativel com
        // modelos especificos: nenhum". Os dados seguem no banco — a API so
        // grava `compatibleVehicleIds` quando ele vem definido.
        .catch(() => setModelsLoadFailed(true));
    } else {
      setCompatMode('universal');
      setSelectedModels([]);
    }
  }, [existing]);

  async function handlePhotoUpload(file: File) {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() as 'jpg' | 'jpeg' | 'png' | 'webp';
      const { uploadUrl, s3Key } = await presignMutation.mutateAsync(ext || 'jpg');
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setPhotoS3Key(s3Key);
      setPhotoUrl(URL.createObjectURL(file));
    } finally {
      setUploading(false);
    }
  }

  async function handleTestLink() {
    if (!buyUrl) return;
    setLinkTestStatus('testing');
    try {
      await fetch(buyUrl, { method: 'HEAD', mode: 'no-cors', signal: AbortSignal.timeout(5000) });
      setLinkTestStatus('ok');
    } catch {
      setLinkTestStatus('error');
    }
  }

  function handleSubmit() {
    const modelIds = compatMode === 'specific' && selectedModels.length > 0
      ? selectedModels.map((m) => m.id)
      : undefined;
    const parsedTags = tags.split(',').map((t) => t.trim()).filter(Boolean);

    const data = {
      name,
      brand: brand || undefined,
      sku: sku || undefined,
      partNumber: partNumber || undefined,
      description: description || undefined,
      category: category || 'geral',
      price: parseFloat(price),
      buyUrl,
      deliveryDaysMin: deliveryMin ? parseInt(deliveryMin) : undefined,
      deliveryDaysMax: deliveryMax ? parseInt(deliveryMax) : undefined,
      compatibleVehicleIds: modelIds,
      tags: parsedTags.length > 0 ? parsedTags : undefined,
      active,
      outOfStock,
    };

    if (productId) {
      updateMutation.mutate(
        { productId, ...data, photoS3Key: photoS3Key || undefined } as Parameters<typeof updateMutation.mutate>[0],
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        { ...data } as Parameters<typeof createMutation.mutate>[0],
        { onSuccess: (product) => { onCreated?.(product.id); onClose(); } },
      );
    }
  }

  // Ver a nota equivalente no ProductDrawer: sem `loadFailed` aqui, salvar a
  // partir do formulario em branco reativava um produto pausado.
  const canSubmit =
    !loadFailed &&
    name.trim().length > 0 &&
    parseFloat(price) > 0 &&
    buyUrl.trim().length > 0 &&
    category.length > 0;

  const inputCls = 'w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-bold">
            {productId ? 'Editar produto' : 'Novo produto no catálogo'}
          </h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted"><X size={18} /></button>
        </div>

        {/* Form */}
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-4">
          {loadFailed && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3" role="alert">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-destructive" />
              <div className="flex-1 text-xs">
                <p className="font-semibold text-destructive">Não foi possível carregar este produto</p>
                <p className="mt-0.5 text-muted-foreground">
                  Os campos estão em branco porque a busca falhou, não porque o produto
                  perdeu os dados. Salvar está bloqueado até eles carregarem.
                </p>
                <button
                  type="button"
                  onClick={() => void retryLoad()}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 font-medium transition-colors hover:bg-muted"
                >
                  <RotateCw size={12} />
                  Tentar de novo
                </button>
              </div>
            </div>
          )}
          {/* Photo */}
          <div>
            <label className="mb-1 block text-sm font-medium">Foto do produto</label>
            <div
              className="flex h-32 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border hover:bg-muted"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : photoUrl ? (
                <img src={photoUrl} alt="Preview" className="h-full rounded-lg object-contain" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload size={20} />
                  <span className="text-xs">Clique para enviar</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); }}
            />
          </div>

          {/* Name + Brand */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Nome *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Pneu Michelin 205/55 R16" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Marca</label>
              <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)}
                placeholder="Ex: Michelin" className={inputCls} />
            </div>
          </div>

          {/* SKU + Part Number */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">SKU</label>
              <input type="text" value={sku} onChange={(e) => setSku(e.target.value)}
                placeholder="Opcional" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Part Number</label>
              <input type="text" value={partNumber} onChange={(e) => setPartNumber(e.target.value)}
                placeholder="Opcional" className={inputCls} />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium">Descrição</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} placeholder="Descrição do produto" className={inputCls} />
          </div>

          {/* Category */}
          <div>
            <label className="mb-1 block text-sm font-medium">Categoria *</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                    category === cat ? 'border-primary bg-accent text-primary font-medium' : 'border-border hover:bg-muted'
                  }`}
                >{cat}</button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="mb-1 block text-sm font-medium">Tags</label>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="Separadas por vírgula (ex: premium, importado)" className={inputCls} />
          </div>

          {/* Price + Buy URL */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Preço (R$) *</label>
              <input type="number" min={0.01} step={0.01} value={price}
                onChange={(e) => setPrice(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">URL de compra *</label>
              <div className="flex gap-2">
                <input type="url" value={buyUrl} onChange={(e) => { setBuyUrl(e.target.value); setLinkTestStatus('idle'); }}
                  placeholder="https://..." className={inputCls} />
                <button type="button" onClick={handleTestLink}
                  disabled={!buyUrl || linkTestStatus === 'testing'}
                  className="shrink-0 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
                >
                  {linkTestStatus === 'testing' ? <Loader2 size={12} className="animate-spin" /> :
                   linkTestStatus === 'ok' ? '✓' :
                   linkTestStatus === 'error' ? '✗' :
                   <ExternalLink size={12} />}
                </button>
              </div>
              {linkTestStatus === 'ok' && <span className="text-xs text-emerald-600 dark:text-emerald-400">Link acessível</span>}
              {linkTestStatus === 'error' && <span className="text-xs text-amber-600 dark:text-amber-400">Link pode estar inacessível (CORS pode interferir)</span>}
            </div>
          </div>

          {/* Delivery */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Prazo mínimo (dias)</label>
              <input type="number" min={0} value={deliveryMin}
                onChange={(e) => setDeliveryMin(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Prazo máximo (dias)</label>
              <input type="number" min={0} value={deliveryMax}
                onChange={(e) => setDeliveryMax(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Compatibility */}
          <div>
            <label className="mb-1 block text-sm font-medium">Compatibilidade</label>
            <div className="flex gap-3 mb-2">
              <button type="button" onClick={() => setCompatMode('universal')}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  compatMode === 'universal' ? 'border-primary bg-accent text-primary font-medium' : 'border-border hover:bg-muted'
                }`}
              >Universal</button>
              <button type="button" onClick={() => setCompatMode('specific')}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  compatMode === 'specific' ? 'border-primary bg-accent text-primary font-medium' : 'border-border hover:bg-muted'
                }`}
              >Específico</button>
            </div>
            {compatMode === 'specific' && (
              <>
                {modelsLoadFailed && (
                  <p className="mb-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">
                    Não foi possível carregar os modelos já cadastrados neste produto.
                    Eles continuam salvos — recarregue a página antes de mexer na
                    compatibilidade, para não substituí-los sem querer.
                  </p>
                )}
                <CatalogMultiSelect selected={selectedModels} onChange={setSelectedModels} />
              </>
            )}
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary" />
              <span className="text-sm">Produto ativo</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={outOfStock} onChange={(e) => setOutOfStock(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary" />
              <span className="text-sm">Fora de estoque</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button onClick={onClose}
            className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={!canSubmit || isPending}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50">
            {isPending ? 'Salvando...' : productId ? 'Salvar alterações' : 'Criar produto'}
          </button>
        </div>
      </div>
    </div>
  );
}
